import React, { MutableRefObject } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { Typography, Chip } from 'Components/atoms';
import { RiDeleteBin6Line } from '@react-icons/all-files/ri/RiDeleteBin6Line';
import { ImPencil } from '@react-icons/all-files/im/ImPencil';
import {
    RULESETS_COL_NAME,
    RULESETS_COL_ID,
    RULESETS_COL_DESCRIPTION,
    RULESETS_COL_CONNECTOR,
    RULESETS_COL_DEFAULT,
    RULESETS_COL_CREATED,
    RULESETS_EDIT_TITLE,
    RULESETS_DELETE_TITLE,
} from 'Core-utils/constants/constants';
import { RuleSetDto } from 'Core-utils/types/RuleSetsConfig.types';

type T = (key: string, fallback?: string) => string;

export function getRuleSetsGridColumns(
    t: T,
    // Ref so cell renderers always read the latest connector map without requiring useMemo re-creation
    connectorMapRef: MutableRefObject<Record<string, string>>,
    onEditClickRef: MutableRefObject<(rs: RuleSetDto) => void>,
    onDeleteClickRef: MutableRefObject<(id: string) => void>,
): ColDef<RuleSetDto>[] {
    return [
        {
            colId: 'name',
            field: 'name',
            headerName: t(RULESETS_COL_NAME, 'NAME'),
            sortable: true,
            width: 200,
            minWidth: 120,
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) => (
                <Chip value={params.value ?? ''} variant="status" />
            ),
        },
        {
            colId: 'id',
            field: 'id',
            headerName: t(RULESETS_COL_ID, 'ID'),
            sortable: true,
            width: 180,
            minWidth: 120,
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) => (
                <Typography variant="body5" className="text-primary-500 rulesets-config__id-code">
                    {params.value ?? '—'}
                </Typography>
            ),
        },
        {
            colId: 'description',
            field: 'description',
            headerName: t(RULESETS_COL_DESCRIPTION, 'DESCRIPTION'),
            sortable: true,
            flex: 1,
            minWidth: 140,
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) =>
                params.value ? (
                    <div title={params.value}>
                        <Typography
                            variant="body5"
                            color="text-high-emphasis"
                            className="rulesets-config__desc-text"
                        >
                            {params.value}
                        </Typography>
                    </div>
                ) : (
                    <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                        —
                    </Typography>
                ),
        },
        {
            colId: 'connector',
            headerName: t(RULESETS_COL_CONNECTOR, 'CONNECTOR'),
            sortable: true,
            width: 192,
            minWidth: 110,
            valueGetter: (p) =>
                p.data?.cid ? connectorMapRef.current[p.data.cid] ?? p.data.cid : '',
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) => {
                const row = params.data;
                if (!row?.cid) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            —
                        </Typography>
                    );
                }
                const label = connectorMapRef.current[row.cid] ?? row.cid;
                return (
                    <div className="d-flex flex-column gap-1">
                        <Typography variant="body5" color="text-high-emphasis">
                            {label}
                        </Typography>
                        <Typography
                            variant="caption1"
                            color="text-low-emphasis"
                            className="rulesets-config__cid-sub"
                        >
                            {row.cid}
                        </Typography>
                    </div>
                );
            },
        },
        {
            colId: 'is_default',
            field: 'is_default',
            headerName: t(RULESETS_COL_DEFAULT, 'DEFAULT'),
            sortable: true,
            width: 100,
            minWidth: 72,
            valueGetter: (p) => (p.data?.is_default ? 1 : 0),
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) => (
                <span
                    className={`rulesets-config__default-badge rulesets-config__default-badge--${
                        params.data?.is_default ? 'on' : 'off'
                    }`}
                >
                    {params.data?.is_default ? 'YES' : 'NO'}
                </span>
            ),
        },
        {
            colId: 'createdAt',
            field: 'createdAt',
            headerName: t(RULESETS_COL_CREATED, 'CREATED'),
            sortable: true,
            width: 160,
            minWidth: 110,
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) => {
                if (!params.value) {
                    return <Typography variant="body5" color="text-low-emphasis">—</Typography>;
                }
                const date = new Date(params.value);
                return (
                    <div className="d-flex flex-column">
                        <Typography variant="body5" color="text-low-emphasis">
                            {date.toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            })}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis">
                            {date.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                            })}
                        </Typography>
                    </div>
                );
            },
        },
        {
            colId: 'rs-actions',
            headerName: '',
            sortable: false,
            width: 96,
            minWidth: 72,
            suppressHeaderMenuButton: true,
            cellRenderer: (params: ICellRendererParams<RuleSetDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <div className="d-flex align-items-center gap-3">
                        <ImPencil
                            size={16}
                            className="rules-icon-btn"
                            title={t(RULESETS_EDIT_TITLE, 'Edit rule set')}
                            onClick={() => onEditClickRef.current(row)}
                        />
                        <RiDeleteBin6Line
                            size={16}
                            className="rules-icon-btn rules-icon-btn--danger"
                            title={t(RULESETS_DELETE_TITLE, 'Delete rule set')}
                            onClick={() => onDeleteClickRef.current(row.id)}
                        />
                    </div>
                );
            },
        },
    ];
}
